import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UI_THEME } from '../ui/theme';
import { notifyError } from '../utils/notify';
import type { ContextoTela } from '../utils/contextoSolicitacao';
import Button from './ui/Button';
import { FIELD_CLASS } from './ui/fieldStyles';
import {
  ANEXO_EXTENSOES,
  ANEXO_TAMANHO_MAXIMO_BYTES,
  CATEGORIAS_NOVAS,
  CATEGORIA_OUTRO_MAXIMO,
  mensagemDeErro,
  solicitacaoService,
  type CategoriaSolicitacao,
  type Solicitacao,
} from '../services/solicitacaoService';

const DESCRICAO_MAXIMA = 4000;

type Props = {
  /**
   * Abertura a partir de uma tela: o alvo (emenda, plano ou pré-projeto) vem da própria tela e fica fixo.
   * Sem contexto, o formulário pergunta a emenda (e o plano) dentro da página do menu.
   */
  contexto?: ContextoTela;
  onCriada: (solicitacao: Solicitacao, falhasDeAnexo: string[]) => void;
  onCancelar?: () => void;
};

/**
 * Formulário de pedido, pensado para quem usa pouco o sistema: uma pergunta de cada vez, botões grandes
 * no lugar de listas, nenhum termo técnico. O servidor é quem decide o que vale (recorte por parlamentar,
 * Direta/Indireta, catálogo de pré-projetos, tamanho e formato do anexo); aqui só se evita o ida-e-volta.
 */
const SolicitacaoForm: React.FC<Props> = ({ contexto, onCriada, onCancelar }) => {
  const queryClient = useQueryClient();
  const isSaas = UI_THEME === 'saas';

  const [emendaId, setEmendaId] = useState('');
  const [planoId, setPlanoId] = useState('');
  const [categoria, setCategoria] = useState<CategoriaSolicitacao | ''>('');
  const [categoriaOutro, setCategoriaOutro] = useState('');
  const [descricao, setDescricao] = useState('');
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [planoDaTela, setPlanoDaTela] = useState('');

  // Só no formulário manual (ou, no contextual, para mostrar o código da emenda da tela).
  const precisaEmendas = !contexto || Boolean(contexto.emendaId || contexto.planoId);
  const emendas = useQuery({
    queryKey: ['solicitacoes', 'emendas'],
    queryFn: solicitacaoService.emendasElegiveis,
    enabled: precisaEmendas,
  });
  const emendaSelecionada = useMemo(
    () => emendas.data?.find((e) => e.id === emendaId),
    [emendas.data, emendaId],
  );
  const emendaDaTela = contexto?.emendaId ? emendas.data?.find((e) => e.id === contexto.emendaId) : undefined;

  const criar = useMutation({
    mutationFn: async () => {
      const criada = await solicitacaoService.criar(
        contexto
          ? {
              emendaId: contexto.emendaId ?? null,
              planoTrabalhoId: contexto.planoId ?? (planoDaTela || null),
              preProjetoId: contexto.preProjetoId ?? null,
              categoria: categoria as CategoriaSolicitacao,
              categoriaOutro: categoria === 'OUTRO' ? categoriaOutro : null,
              descricao,
              rota: contexto.rota,
              tituloTela: contexto.tituloTela,
            }
          : {
              emendaId,
              planoTrabalhoId: planoId || null,
              categoria: categoria as CategoriaSolicitacao,
              categoriaOutro: categoria === 'OUTRO' ? categoriaOutro : null,
              descricao,
            },
      );
      const falhas: string[] = [];
      for (const arquivo of arquivos) {
        try {
          await solicitacaoService.anexar(criada.id, arquivo);
        } catch (err) {
          falhas.push(`${arquivo.name}: ${mensagemDeErro(err, 'não foi possível enviar')}`);
        }
      }
      return { criada, falhas };
    },
    onSuccess: ({ criada, falhas }) => {
      queryClient.invalidateQueries({ queryKey: ['solicitacoes'] });
      onCriada(criada, falhas);
    },
    onError: (err) => notifyError(mensagemDeErro(err, 'Não foi possível enviar o pedido. Tente de novo.')),
  });

  const escolherArquivos = (lista: FileList | null) => {
    const aceitos: File[] = [];
    Array.from(lista ?? []).forEach((f) => {
      const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
      if (!ANEXO_EXTENSOES.includes(ext)) {
        notifyError(`${f.name}: formato não aceito. Use ${ANEXO_EXTENSOES.join(', ')}.`);
      } else if (f.size > ANEXO_TAMANHO_MAXIMO_BYTES) {
        notifyError(`${f.name}: é maior que 5 MB.`);
      } else {
        aceitos.push(f);
      }
    });
    setArquivos(aceitos);
  };

  const enviar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!contexto && !emendaId) return notifyError('Escolha a emenda.');
    if (!categoria) return notifyError('Escolha o que você precisa.');
    if (categoria === 'OUTRO' && !categoriaOutro.trim()) return notifyError('Diga qual é o assunto.');
    if (!descricao.trim()) return notifyError('Explique com suas palavras o que você precisa.');
    criar.mutate();
  };

  const label = 'mb-2 block text-base font-semibold text-slate-800 dark:text-slate-100';
  const input = `${FIELD_CLASS} !px-4 !py-3 !text-base`;
  const escolhida = isSaas
    ? 'border-teal-600 bg-teal-50 text-teal-900 ring-2 ring-teal-200 dark:border-teal-400 dark:bg-teal-500/15 dark:text-teal-100 dark:ring-teal-900/40'
    : 'border-blue-700 bg-blue-50 text-blue-900 ring-2 ring-blue-200 dark:border-blue-400 dark:bg-blue-500/15 dark:text-blue-100 dark:ring-blue-900/40';
  const naoEscolhida =
    'border-slate-300 bg-white text-slate-800 hover:border-slate-400 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800';

  if (!contexto) {
    if (emendas.isLoading) {
      return <p className="text-base text-slate-500 dark:text-slate-400">Carregando suas emendas...</p>;
    }
    if (emendas.isError) {
      return <p className="text-base text-red-600 dark:text-red-400">Não foi possível carregar suas emendas. Tente de novo em instantes.</p>;
    }
    if (!emendas.data || emendas.data.length === 0) {
      return (
        <p className="text-base leading-7 text-slate-600 dark:text-slate-300">
          Não encontramos emendas no seu nome. Se você esperava ver emendas aqui, fale com a equipe da
          Prefeitura para conferir o seu cadastro.
        </p>
      );
    }
  }

  const sobreO = contexto?.emendaId
    ? `a emenda${emendaDaTela?.codigo ? ` ${emendaDaTela.codigo}` : ''}`
    : contexto?.planoId
      ? 'este plano de trabalho'
      : contexto?.preProjetoId
        ? 'este pré-projeto'
        : 'esta tela';
  const planosDaTela = contexto?.emendaId && emendaDaTela?.aceitaPlano ? emendaDaTela.planos : [];

  return (
    <form onSubmit={enviar} className="flex flex-col">
      <div className="space-y-6">
        {!contexto && (
          <div>
            <label htmlFor="sol-emenda" className={label}>Sobre qual emenda?</label>
            <select
              id="sol-emenda"
              className={input}
              value={emendaId}
              onChange={(e) => {
                setEmendaId(e.target.value);
                setPlanoId('');
              }}
            >
              <option value="">Escolha a emenda...</option>
              {emendas.data?.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.codigo}{e.objeto ? ` — ${e.objeto.slice(0, 60)}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <div role="radiogroup" aria-labelledby="sol-categoria-titulo">
          <p id="sol-categoria-titulo" className={label}>
            {contexto ? `O que você precisa sobre ${sobreO}?` : 'O que você precisa?'}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {CATEGORIAS_NOVAS.map((c) => {
              const ativa = categoria === c.value;
              return (
                <button
                  key={c.value}
                  type="button"
                  role="radio"
                  aria-checked={ativa}
                  onClick={() => setCategoria(c.value)}
                  className={`min-h-[56px] rounded-xl border-2 px-3 py-2 text-left leading-tight text-base font-semibold transition ${ativa ? escolhida : naoEscolhida}`}
                >
                  {c.botao}
                </button>
              );
            })}
          </div>
        </div>

        {categoria === 'OUTRO' && (
          <div>
            <label htmlFor="sol-categoria-outro" className={label}>Qual assunto?</label>
            <input
              id="sol-categoria-outro"
              className={input}
              maxLength={CATEGORIA_OUTRO_MAXIMO}
              value={categoriaOutro}
              onChange={(e) => setCategoriaOutro(e.target.value)}
              placeholder="Ex.: incluir mais uma instituição"
            />
          </div>
        )}

        <div>
          <label htmlFor="sol-descricao" className={label}>Explique com suas palavras</label>
          <textarea
            id="sol-descricao"
            className={`${input} min-h-[140px] leading-7`}
            maxLength={DESCRICAO_MAXIMA}
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder="Escreva aqui o que você precisa. Não precisa ser formal."
          />
        </div>

        <details className="rounded-xl border border-slate-200 dark:border-slate-700">
            <summary className="flex min-h-[48px] cursor-pointer items-center px-4 text-base font-semibold text-slate-700 dark:text-slate-200">
              Mais opções
            </summary>
            <div className="space-y-5 border-t border-slate-200 p-4 dark:border-slate-700">
              {!contexto && emendaSelecionada?.aceitaPlano && (
                <div>
                  <label htmlFor="sol-plano" className={label}>Sobre algum plano de trabalho?</label>
                  <select id="sol-plano" className={input} value={planoId} onChange={(e) => setPlanoId(e.target.value)}>
                    <option value="">Não, só sobre a emenda</option>
                    {emendaSelecionada.planos.map((p) => (
                      <option key={p.id} value={p.id}>{p.titulo || p.id}</option>
                    ))}
                  </select>
                </div>
              )}

              {planosDaTela.length > 0 && (
                <div>
                  <label htmlFor="sol-plano-tela" className={label}>Sobre algum plano de trabalho?</label>
                  <select id="sol-plano-tela" className={input} value={planoDaTela} onChange={(e) => setPlanoDaTela(e.target.value)}>
                    <option value="">Não, só sobre a emenda</option>
                    {planosDaTela.map((p) => (
                      <option key={p.id} value={p.id}>{p.titulo || p.id}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label htmlFor="sol-anexos" className={label}>Juntar um arquivo</label>
                <input
                  id="sol-anexos"
                  type="file"
                  multiple
                  accept={ANEXO_EXTENSOES.map((x) => `.${x}`).join(',')}
                  onChange={(e) => escolherArquivos(e.target.files)}
                  className="block w-full text-base text-slate-600 dark:text-slate-300"
                />
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Até 5 MB por arquivo.</p>
                {arquivos.length > 0 && (
                  <ul className="mt-2 list-disc pl-5 text-sm text-slate-600 dark:text-slate-300">
                    {arquivos.map((f) => <li key={f.name}>{f.name}</li>)}
                  </ul>
                )}
              </div>
            </div>
        </details>
      </div>

      <div className="sticky bottom-0 -mx-1 mt-6 flex flex-col-reverse gap-3 border-t border-slate-200 bg-white px-1 pb-1 pt-4 dark:border-slate-700 dark:bg-slate-900 sm:flex-row sm:justify-end">
        {onCancelar && (
          <Button variant="secondary" className="min-h-[48px] px-6 text-base" onClick={onCancelar} disabled={criar.isPending}>
            Cancelar
          </Button>
        )}
        <Button type="submit" variant="primary" className="min-h-[48px] px-6 text-base" disabled={criar.isPending}>
          {criar.isPending ? 'Enviando...' : 'Enviar pedido'}
        </Button>
      </div>
    </form>
  );
};

export default SolicitacaoForm;
